import React from "react";
import Foundation from "@fast/fast-components-foundation-react";
import { classNames } from "@microsoft/fast-web-utilities";
import { memoize } from "lodash-es";
import { ColorRGBA64, parseColorHexRGB } from "@microsoft/fast-colors";
import { DesignSystemConsumer } from "@fast/fast-jss-manager-react";
import { ImagePosition, } from "./article.props";
class Article extends Foundation {
    constructor(props) {
        super(props);
        this.handledProps = {
            headingLevel: void 0,
            href: void 0,
            target: void 0,
            imagePosition: void 0,
            wide: void 0,
            card: void 0,
        };
        /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
        this.cloneContentIndicator = () => {
            return React.cloneElement(this.getContentIndicator(this.props.children)[0], {
                expanded: this.state.articleFocused || this.state.articleHover ? true : false,
            });
        };
        this.generateGradient = memoize((color, direction) => {
            const { r, g, b } = parseColorHexRGB(color);
            const gradientMidwayColor = new ColorRGBA64(r, g, b, this.hasElementSlot("image") ? 0.8 : 0.6).toStringWebRGBA();
            let gradientString = "";
            // If wide prop is true the card prop will always also be true
            if (this.props.wide) {
                if (!this.hasElementSlot("image")) {
                    gradientString = `linear-gradient( ${direction === "ltr" ? "44.24deg" : "317deg"}, ${color} 0%, ${gradientMidwayColor} 100%), #FFF`;
                }
                else if (this.props.imagePosition === "start") {
                    gradientString = `linear-gradient(  ${direction === "ltr" ? "90deg" : "270deg"}, transparent 0%, ${gradientMidwayColor}119px, ${color} 274px)`;
                }
                else {
                    gradientString = `linear-gradient( ${direction === "ltr" ? "270deg" : "90deg"}, transparent 0%, ${gradientMidwayColor} 119px, ${color}  274px)`;
                }
            }
            else if (this.props.card && !this.props.wide) {
                if (!this.hasElementSlot("image")) {
                    gradientString = `linear-gradient(${direction === "ltr" ? "44.24deg" : "317deg"}, ${color} 0%, ${gradientMidwayColor} 100% ), #FFF`;
                }
                else {
                    gradientString = `linear-gradient(to bottom, transparent 0%, ${gradientMidwayColor} 60.64%, ${color} 100%)`;
                }
            }
            return gradientString;
        });
        // Background style for gradient on card
        this.gradientStyle = (designSystem) => {
            return this.props.card && designSystem
                ? {
                    background: this.generateGradient(designSystem.backgroundColor, designSystem.direction),
                }
                : {};
        };
        this.state = {
            isCardImage: this.props.card || false,
            articleFocused: false,
            articleHover: false,
        };
        this.articleCardRef = React.createRef();
    }
    /**
     * Renders the component
     */
    render() {
        const { article_text, article_gradient, article_attribution, article_heading, article_abstract, article_actions, article_startActions, article_endActions, article_image, } = this.props.managedClasses;
        return (React.createElement(DesignSystemConsumer, null, (designSystem) => {
            return (React.createElement("div", { className: this.generateClassNames(), ref: this.articleCardRef },
                this.props.imagePosition === "start" && (React.createElement("div", { className: article_image }, this.withSlot("image"))),
                this.withSlot("start"),
                React.createElement("div", { className: article_text },
                    React.createElement("div", { className: article_gradient, style: this.gradientStyle(designSystem) }),
                    React.createElement("div", { className: article_attribution }, this.withSlot("attribution")),
                    React.createElement("a", Object.assign({}, this.props.headingTelemetry, { id: "heading", className: article_heading, role: "heading", "aria-level": this.props.headingLevel, href: this.props.href, target: this.props.target }), this.withoutSlot([
                        "attribution",
                        "abstract",
                        "end-action",
                        "hide-story",
                        "image",
                        "start",
                        "start-action",
                        "content-indicator",
                    ])),
                    React.createElement("div", { className: article_abstract }, this.withSlot("abstract")),
                    React.createElement("div", { className: article_actions },
                        React.createElement("div", { className: article_startActions }, this.withSlot("start-action")),
                        React.createElement("div", { className: article_endActions }, this.withSlot("end-action")))),
                this.props.imagePosition === "end" && (React.createElement("div", { className: article_image }, this.withSlot("image"))),
                this.renderHideStory(designSystem),
                this.renderContentIndicator()));
        }));
    }
    /**
     * Component did mount
     */
    componentDidMount() {
        // focus-within is not supported for Edge legacy, so I have to setup listeners to handle focus events triggered in child elements
        if (this.articleCardRef && this.articleCardRef.current) {
            this.articleCardRef.current.addEventListener("focusin", () => {
                this.setState({ articleFocused: true });
            });
            this.articleCardRef.current.addEventListener("focusout", () => {
                this.setState({ articleFocused: false });
            });
            this.articleCardRef.current.addEventListener("mouseover", () => {
                this.setState({ articleHover: true });
            });
            this.articleCardRef.current.addEventListener("mouseout", () => {
                this.setState({ articleHover: false });
            });
        }
    }
    /**
     * Generates class names
     */
    generateClassNames() {
        const { article, article__card, article__card_focusWithin, article__card_noImage, article__wide, article__wide_imageStart, article__wide_noImage, article__card_contentIndicator_noImage, article__card_contentIndicator_wide, } = this.props.managedClasses;
        return super.generateClassNames(classNames(article, [article__card, this.props.card], [article__card_focusWithin, this.state.articleFocused], [article__card_noImage, this.props.card && !this.hasElementSlot("image")], [article__wide, this.props.wide], [article__wide_noImage, this.props.wide && !this.hasElementSlot("image")], [
            article__wide_imageStart,
            this.props.card && this.props.imagePosition === "start",
        ], [
            article__card_contentIndicator_noImage,
            !this.hasElementSlot("image") &&
                this.hasElementSlot("content-indicator"),
        ], [
            article__card_contentIndicator_wide,
            this.props.wide &&
                this.hasElementSlot("image") &&
                this.hasElementSlot("content-indicator"),
        ]));
    }
    renderHideStory(designSystem) {
        if (this.hasElementSlot("hide-story")) {
            const { article_hideStoryWrapper, article_hideStory, } = this.props.managedClasses;
            return (React.createElement("div", { className: article_hideStoryWrapper },
                React.createElement("div", { className: article_hideStory }, this.withSlot("hide-story"))));
        }
    }
    renderContentIndicator() {
        if (this.hasElementSlot("content-indicator")) {
            return (React.createElement("div", { className: this.props.managedClasses.article_contentIndicator }, this.cloneContentIndicator()));
        }
    }
    getContentIndicator(children) {
        return this.filterChildren(this.withSlot("content-indicator", children));
    }
    filterChildren(nodes) {
        if (Array.isArray(nodes)) {
            return nodes.filter(Boolean);
        }
        else {
            return nodes;
        }
    }
    hasElementSlot(slot) {
        if (!this.props.children) {
            return false;
        }
        const elementsWithSlot = this.withSlot(slot);
        return (React.Children.toArray(elementsWithSlot).filter((child) => child !== null).length > 0);
    }
}
Article.displayName = "Article";
Article.defaultProps = {
    imagePosition: ImagePosition.end,
    wide: false,
    card: false,
};
export default Article;
export * from "./article.props";
